import { useEffect, useState } from "react";
import { Stats, Prospects, Appointments } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendUp, Users, Envelope, CalendarCheck, ChatCircleDots, CheckCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const statusColors = {
  new: "bg-neutral-100 text-neutral-700",
  contacted: "bg-blue-50 text-blue-700",
  replied: "bg-amber-50 text-amber-700",
  booked: "bg-emerald-50 text-emerald-700",
  closed: "bg-purple-50 text-purple-700",
  unqualified: "bg-neutral-100 text-neutral-500",
};

function Metric({ label, value, sublabel, icon: Icon, testId }) {
  return (
    <Card className="p-6 rounded-md border-neutral-200 bg-white hover:border-neutral-400 transition-colors duration-200" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={16} weight="duotone" className="text-neutral-400" />
      </div>
      <div className="metric-num text-6xl mt-4">{value}</div>
      {sublabel && <div className="text-xs text-neutral-500 mt-2 font-mono">{sublabel}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentProspects, setRecentProspects] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    Stats.get().then(setStats).catch(() => {});
    Prospects.list().then(list => setRecentProspects(list.slice(0, 6))).catch(() => {});
    Appointments.list().then(list => {
      const now = new Date().toISOString();
      setUpcoming(list.filter(a => a.starts_at >= now).slice(0, 5));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline="Overview · Realtime"
        title="Command Center"
        description="Your prospecting pipeline, live. Discover leads, ship messages, close meetings."
        testId="dashboard-header"
      />

      <div className="p-10 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in" data-testid="metrics-grid">
          <Metric label="Total Prospects" value={stats?.total_prospects ?? "—"} sublabel="in pipeline" icon={Users} testId="metric-prospects" />
          <Metric label="Messages Sent" value={stats?.messages_sent ?? "—"} sublabel={`${stats?.contacted ?? 0} contacted`} icon={Envelope} testId="metric-messages" />
          <Metric label="Reply Rate" value={stats ? `${stats.reply_rate}%` : "—"} sublabel="of contacted" icon={ChatCircleDots} testId="metric-reply-rate" />
          <Metric label="Meetings Booked" value={stats?.booked ?? "—"} sublabel={`${stats?.upcoming_appointments ?? 0} upcoming`} icon={CalendarCheck} testId="metric-booked" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 rounded-md border-neutral-200" data-testid="recent-prospects-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Recently Added</div>
                <div className="font-display text-2xl mt-1">Latest prospects</div>
              </div>
              <Link to="/prospects" className="text-xs font-mono text-neutral-500 hover:text-neutral-900 uppercase tracking-widest" data-testid="link-all-prospects">View all →</Link>
            </div>
            <div className="border-t border-neutral-200">
              {recentProspects.length === 0 && (
                <div className="py-10 text-center text-sm text-neutral-500">No prospects yet. Add or scrape some to begin.</div>
              )}
              {recentProspects.map(p => (
                <div key={p.id} className="flex items-center justify-between py-3 border-b border-neutral-100 row-hover px-2 -mx-2 rounded" data-testid={`prospect-row-${p.id}`}>
                  <div>
                    <div className="text-sm font-medium">{p.business_name}</div>
                    <div className="text-xs text-neutral-500 font-mono">{[p.city, p.industry].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <Badge variant="outline" className={`${statusColors[p.status]} border-0 uppercase tracking-wider text-[10px] font-mono`}>{p.status}</Badge>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 rounded-md border-neutral-200" data-testid="upcoming-card">
            <div className="overline">Upcoming</div>
            <div className="font-display text-2xl mt-1 mb-4">Meetings this week</div>
            <div className="space-y-3">
              {upcoming.length === 0 && (
                <div className="text-sm text-neutral-500 py-6 text-center">No meetings scheduled.</div>
              )}
              {upcoming.map(a => (
                <div key={a.id} className="p-3 border border-neutral-200 rounded-md hover:border-neutral-900 transition-colors" data-testid={`upcoming-${a.id}`}>
                  <div className="text-xs font-mono text-neutral-500 uppercase tracking-widest">
                    {new Date(a.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-sm font-medium mt-1">{a.title}</div>
                  <div className="text-xs text-neutral-500">with {a.prospect_name}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card className="p-6 rounded-md border-neutral-200 bg-neutral-900 text-white" data-testid="pipeline-strip">
          <div className="overline text-neutral-400">Pipeline</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6 mt-4">
            {[
              ["Total", stats?.total_prospects, Users],
              ["Contacted", stats?.contacted, Envelope],
              ["Replied", stats?.replied, ChatCircleDots],
              ["Booked", stats?.booked, CalendarCheck],
              ["Closed", stats?.closed, CheckCircle],
              ["Booking %", stats ? `${stats.booking_rate}%` : "—", TrendUp],
            ].map(([label, value, Icon]) => (
              <div key={label}>
                <div className="flex items-center gap-2 text-neutral-400">
                  <Icon size={14} weight="duotone" />
                  <div className="overline text-neutral-400">{label}</div>
                </div>
                <div className="metric-num text-4xl mt-2 text-white">{value ?? "—"}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
